/**
 * Shared utility methods for AWP DMS Interface request factories.
 */
import {ICase, IFlatRateUnit, IJob, IPackage, IPart, ISublet, IUser} from '@service-and-repairs/awpintegrationlib';
import {PackageType} from '@service-and-repairs/dms-api';
import {CostSplit} from '../models/common/cost-split';
import {DmsFlatRateUnit} from '../models/common/dms-flat-rate-unit';
import {DmsPart} from '../models/common/dms-part';
import {DmsSublet} from '../models/common/dms-sublet';
import {FunctionalHeader} from '../models/common/functional-header';
import {OfferPrice} from '../models/common/offer-price';
import {ServicePackage} from '../models/common/service-package';
import {TechnicalHeaderInRequest} from '../models/common/technical-header-in-request';
import {UserInformation} from '../models/common/user-information';
import {ServiceOrderJob} from '../models/order';

export class AwpDmsInterfaceFactoryUtils {

  static createFunctionalHeader(serviceCase: ICase): FunctionalHeader {
    return this.createFunctionalHeaderFromPrimitives(
      serviceCase.getAssignedDistributionPartnerNumber(),
      serviceCase.getAssignedOutletNumber()
    );
  }

  static createFunctionalHeaderFromPrimitives(dpNo: string, outletNo: string): FunctionalHeader {
    const header = new FunctionalHeader();
    header.distributionPartnerNumber = dpNo;
    header.outletNumber = outletNo;
    return header;
  }

  static createTechnicalHeaderInRequest(schemaName: string, schemaVersion: string): TechnicalHeaderInRequest {
    const header = new TechnicalHeaderInRequest();
    header.requestingApplicationId = 'awp-web';
    header.requestId = ''; // Set in the service layer
    header.schemaName = schemaName;
    header.schemaVersion = schemaVersion;
    header.sessionId = ''; // Set in the service layer
    return header;
  }

  static createUserInformation(user: IUser): UserInformation {
    return this.createUserInformationFromPrimitive(user.getLocale() || 'en-US');
  }

  static createUserInformationFromPrimitive(locale: string): UserInformation {
    const userInfo = new UserInformation();
    userInfo.locale = locale;
    return userInfo;
  }

  static createBrandString(brand: string): string | null {
    const brandMap: Record<string, string> = {
      'BMW': 'BMW',
      'BMW i': 'BMW_I',
      'MINI': 'MINI',
      'MOT': 'MOT',
      'RR': 'RR',
      'ZINORO': 'ZINORO'
    };
    return brandMap[brand] ?? 'BMW';
  }

  static getCostSplitsForJob(job: IJob): CostSplit[] {
    if (job.getPackages().some((pack: IPackage): boolean => pack.getType() === PackageType.Grp)) {
      return [{costUnit: 'WARRANTY', percentage: 100}];
    } else if (job.getPackages().some((pack: IPackage): boolean => pack.getType() === PackageType.Sip)) {
      return [{costUnit: 'SERVICE_INCLUSIVE', percentage: 100}];
    } else if (job.getDemandCategory() === 'TEC') {
      return [{costUnit: 'WARRANTY', percentage: 100}];
    } else {
      return [];
    }
  }

  static createOfferPrice(totalCustomerGrossPrice: number, currencyCode: string): OfferPrice {
    const offerPrice = new OfferPrice();
    offerPrice.currencyCodeIso4217 = currencyCode;
    offerPrice.isFixedPrice = true;
    offerPrice.offerPriceGross = totalCustomerGrossPrice;
    return offerPrice;
  }

  // --- Shared converters for jobs and their nested lists (high-level to low-level) ---

  static createServiceOrderJob(job: IJob, currencyCode: string): ServiceOrderJob {
    const orderJob = new ServiceOrderJob();
    orderJob.jobDescription = job.getTitle() || 'Service Job';
    orderJob.externalId = job.getExternalId();
    orderJob.costEstimateRequired = job.getIsCostEstimateRequired();
    orderJob.repeatedRepair = job.getIsRepeatVisit();
    orderJob.towedInVehicle = job.getIsBreakdownCase();

    const costSplits = this.getCostSplitsForJob(job);
    if (job.getPackages()?.length > 0) {
      orderJob.packageList = this.createPackageList(job.getPackages(), costSplits, currencyCode);
    } else {
      orderJob.flatRateUnitList = this.createFlatRateUnitList(job.getFlatRateUnits(), costSplits, currencyCode);
      orderJob.partList = this.createPartList(job.getParts(), costSplits, currencyCode);
      orderJob.subletList = this.createSubletList(job.getSublets(), costSplits, currencyCode);
    }
    return orderJob;
  }

  static createPackageList(packages: IPackage[], costSplits: CostSplit[], currencyCode: string): ServicePackage[] {
    return packages.map(p => ({
      campaign: p.getCampaignNumber() ? {
        campaignName: p.getCampaignName(),
        campaignNumber: p.getCampaignNumber(),
        campaignPriceType: 'LumpSum'
      } : null,
      costSplitList: costSplits,
      description: p.getDescription(),
      flatRateUnitList: this.createFlatRateUnitList(p.getFlatRateUnits(), costSplits, currencyCode),
      isBindingLumpSum: p.getType() === 'GRP' || p.getType() === 'SIP' || p.getType() === 'MKP',
      isLocal: p.getIsLocal(),
      number: p.getNumber(),
      offerPrice: this.createOfferPriceFromPackage(p, currencyCode),
      packageType: p.getType(),
      partList: this.createPartList(p.getParts(), costSplits, currencyCode),
      subletList: this.createSubletList(p.getSublets(), costSplits, currencyCode)
    }));
  }

  static createFlatRateUnitList(flatRateUnits: IFlatRateUnit[],
                                costSplits: CostSplit[],
                                currencyCode: string): DmsFlatRateUnit[] {
    return flatRateUnits.map(fru => ({
      costSplitList: costSplits,
      description: fru.getDescription(),
      flatRateUnitType: fru.getType(),
      isLocal: fru.getIsLocal(),
      number: fru.getNumber(),
      offerPrice: this.createOfferPriceFromFru(fru, currencyCode),
      quantity: fru.getAmount()
    }));
  }

  static createPartList(parts: IPart[], costSplits: CostSplit[], currencyCode: string): DmsPart[] {
    return parts.map(part => ({
      costSplitList: costSplits,
      description: part.getDescription(),
      isLocal: part.getIsLocal(),
      number: part.getPartNumber(),
      offerPrice: this.createOfferPriceFromPart(part, currencyCode),
      quantity: part.getQuantity(),
      unit: 'pcs'
    }));
  }

  static createSubletList(sublets: ISublet[], costSplits: CostSplit[], currencyCode: string): DmsSublet[] {
    return sublets.map((sublet: ISublet): DmsSublet => ({
      costSplitList: costSplits,
      description: sublet.getDescription(),
      guid: sublet.getExternalId(),
      offerPrice: this.createOfferPriceFromSublet(sublet, currencyCode),
      quantity: sublet.getQuantity(),
      subletCode: sublet.getSubletCode()
    }));
  }

  // --- OfferPrice helpers ---

  static createOfferPriceFromPackage(pkg: IPackage, currencyCode: string): OfferPrice | null {
    const grossPrice = pkg.getGrossPrice();
    return grossPrice != null ? this.createOfferPrice(grossPrice, currencyCode) : null;
  }

  static createOfferPriceFromFru(fru: IFlatRateUnit, currencyCode: string): OfferPrice | null {
    const grossPrice = fru.getGrossPrice();
    return grossPrice != null ? this.createOfferPrice(grossPrice, currencyCode) : null;
  }

  static createOfferPriceFromPart(part: IPart, currencyCode: string): OfferPrice | null {
    const grossPrice = part.getTotalCustomerGrossPrice();
    return grossPrice != null ? this.createOfferPrice(grossPrice, currencyCode) : null;
  }

  static createOfferPriceFromSublet(sublet: ISublet, currencyCode: string): OfferPrice | null {
    const price = sublet.getPrice();
    return price != null ? this.createOfferPrice(price, currencyCode) : null;
  }
}
